import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, CircleAlert, RefreshCw } from "lucide-react";
import { Button } from "@/ui/button";
import { FormCard, FormStackedRow } from "@/components/settings/settings-ui";
import { formatRelativeTime } from "@/utils";
import { CaptureActivityPanel } from "@/components/spoken-memory/CaptureActivityPanel";
import {
  type CaptureActivity,
  type PendingAudioChunkGroup,
  type ProcessingWindowSummary,
  type SessionAudioChunk,
  type SpeakerClusterReviewItem,
  type SpokenMemoryApi,
  type SpokenMemorySessionDetail,
  type SpokenMemorySessionSummary,
  type VoiceProfileDiagnostics,
  type VoiceProfile,
  spokenMemoryApi,
  useDeleteVoiceProfileMutation,
} from "@/data/spoken-memory";

type Tab = "sessions" | "windows" | "voice";

interface PropsIface {
  onClose: () => void;
  api?: SpokenMemoryApi;
  workspaceSlug?: string | null;
}

export function SpokenMemoryBrowserPage({
  onClose,
  api = spokenMemoryApi,
  workspaceSlug,
}: PropsIface) {
  const [tab, setTab] = useState<Tab>("sessions");
  const [sessions, setSessions] = useState<SpokenMemorySessionSummary[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null,
  );
  const [sessionDetail, setSessionDetail] =
    useState<SpokenMemorySessionDetail | null>(null);
  const [processingWindows, setProcessingWindows] = useState<
    ProcessingWindowSummary[]
  >([]);
  const [reviewItems, setReviewItems] = useState<SpeakerClusterReviewItem[]>(
    [],
  );
  const [claimingClusterId, setClaimingClusterId] = useState<string | null>(
    null,
  );
  const [pendingAudio, setPendingAudio] = useState<PendingAudioChunkGroup[]>(
    [],
  );
  const [captureActivity, setCaptureActivity] = useState<CaptureActivity[]>([]);
  const [voiceProfiles, setVoiceProfiles] = useState<VoiceProfile[]>([]);
  const [voiceDiagnostics, setVoiceDiagnostics] =
    useState<VoiceProfileDiagnostics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [canManageSpokenMemory, setCanManageSpokenMemory] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    const [sessionResult, profileResult, diagnosticsResult, activityResult] =
      await Promise.all([
        api.listSessions(),
        api.listVoiceProfiles(),
        api.getVoiceProfileDiagnostics().catch(() => null),
        api.listCaptureActivity().catch(() => ({ capture_activity: [] })),
      ]);
    const canManage = sessionResult.can_manage_spoken_memory;
    const [windowResult, pendingResult, reviewResult] = canManage
      ? await Promise.all([
          api.listProcessingWindows(),
          api.listPendingAudio(),
          api.listSpeakerClusterReviews(),
        ])
      : [
          { processing_windows: [] },
          { pending_audio: [] },
          { speaker_clusters: [] },
        ];
    setCanManageSpokenMemory(canManage);
    setSessions(sessionResult.captures ?? sessionResult.sessions);
    setCaptureActivity(activityResult.capture_activity);
    setVoiceProfiles(profileResult.voice_profiles);
    setVoiceDiagnostics(diagnosticsResult);
    setProcessingWindows(windowResult.processing_windows);
    setPendingAudio(pendingResult.pending_audio);
    setReviewItems(reviewResult.speaker_clusters);
    setSelectedSessionId(
      (current) =>
        current ??
        (sessionResult.captures ?? sessionResult.sessions)[0]?.id ??
        null,
    );
  }, [api]);

  useEffect(() => {
    void refresh().catch((err: unknown) => setError(String(err)));
  }, [refresh]);

  useEffect(() => {
    if (!canManageSpokenMemory && tab === "windows") {
      setTab("sessions");
    }
  }, [canManageSpokenMemory, tab]);

  useEffect(() => {
    if (!selectedSessionId) {
      setSessionDetail(null);
      return;
    }
    void api
      .getSession(selectedSessionId)
      .then(setSessionDetail)
      .catch((err: unknown) => setError(String(err)));
  }, [api, selectedSessionId]);

  const seedFixture = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.seedFixture();
      await refresh();
      setSelectedSessionId(result.sessionId);
      setTab("sessions");
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  };

  const finalizePendingAudio = async (
    deviceId: string,
    processNow: boolean,
  ) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.manualFinalizeProcessingWindow({
        deviceId,
        processNow,
      });
      await refresh();
      if (result.processed?.sessionId) {
        setSelectedSessionId(result.processed.sessionId);
        setTab("sessions");
      } else {
        setTab("windows");
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  };

  const processWindow = async (windowId: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.processProcessingWindow(windowId);
      await refresh();
      setSelectedSessionId(result.processed.sessionId);
      setTab("sessions");
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  };

  const claimSpeakerCluster = async (clusterId: string) => {
    setClaimingClusterId(clusterId);
    setError(null);
    try {
      await api.claimSpeakerCluster(clusterId);
      await refresh();
      if (sessionDetail?.speaker_clusters.some((c) => c.id === clusterId)) {
        const refreshed = await api.getSession(sessionDetail.session.id);
        setSessionDetail(refreshed);
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setClaimingClusterId(null);
    }
  };

  const tabs = [
    ["sessions", "Captures"],
    ...(canManageSpokenMemory
      ? ([["windows", "Processing Windows"]] as const)
      : []),
    ["voice", "Voice Profiles"],
  ] as const;
  const activeTab =
    tab === "windows" && !canManageSpokenMemory ? "sessions" : tab;

  return (
    <div className="flex h-full min-h-0 flex-col bg-wiki-bg">
      <header className="hairline-b flex items-center justify-between px-6 py-4">
        <div>
          <div className="label-caps">Spoken Memory</div>
          <h1 className="text-xl font-semibold text-wiki-fg-1">
            Captured in-person context for humans and agents
          </h1>
        </div>
        <div className="flex gap-2">
          {canManageSpokenMemory ? (
            <Button
              variant="secondary"
              onClick={seedFixture}
              disabled={loading}
            >
              Seed fixture
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </header>

      <div
        role="tablist"
        aria-label="Spoken memory sections"
        className="hairline-b flex gap-1 px-6 py-2"
      >
        {tabs.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`sm-tab-${key}`}
            aria-selected={activeTab === key}
            aria-controls="sm-tabpanel"
            onClick={() => setTab(key)}
            className={`rounded-md px-3 py-1.5 text-sm ${
              activeTab === key
                ? "bg-wiki-bg-hover font-medium text-wiki-fg-1"
                : "text-wiki-fg-3 hover:bg-wiki-bg-3 hover:text-wiki-fg-1"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {error ? (
        <div className="mx-6 mt-4 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
          {error}
        </div>
      ) : null}

      <main
        id="sm-tabpanel"
        role="tabpanel"
        aria-labelledby={`sm-tab-${activeTab}`}
        className="min-h-0 flex-1 overflow-y-auto px-6 py-5"
      >
        {activeTab === "sessions" ? (
          <SessionsTab
            api={api}
            sessions={sessions}
            captureActivity={captureActivity}
            selectedSessionId={selectedSessionId}
            onSelect={setSelectedSessionId}
            detail={sessionDetail}
          />
        ) : activeTab === "windows" ? (
          <ProcessingWindowsTab
            windows={processingWindows}
            pendingAudio={pendingAudio}
            loading={loading}
            onFinalize={finalizePendingAudio}
            onProcessWindow={processWindow}
          />
        ) : activeTab === "voice" ? (
          <VoiceProfilesTab
            api={api}
            workspaceSlug={workspaceSlug}
            profiles={voiceProfiles}
            diagnostics={voiceDiagnostics}
            reviewItems={canManageSpokenMemory ? reviewItems : []}
            showReviewQueue={canManageSpokenMemory}
            claimingClusterId={claimingClusterId}
            onClaimCluster={claimSpeakerCluster}
            onRefreshDiagnostics={refresh}
            onUploaded={refresh}
          />
        ) : null}
      </main>
    </div>
  );
}

function formatDateTime(value: number) {
  return new Date(Number(value)).toLocaleString();
}

function formatDuration(ms: number) {
  const totalSeconds = Math.max(0, Math.round(Number(ms) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

function formatConfidence(value: number | null) {
  if (value == null || !Number.isFinite(value)) return "n/a";
  return `${Math.round(value * 100)}%`;
}

function SessionsTab({
  api = spokenMemoryApi,
  sessions,
  captureActivity,
  selectedSessionId,
  onSelect,
  detail,
}: {
  api?: SpokenMemoryApi;
  sessions: SpokenMemorySessionSummary[];
  captureActivity: CaptureActivity[];
  selectedSessionId: string | null;
  onSelect: (id: string) => void;
  detail: SpokenMemorySessionDetail | null;
}) {
  return (
    <div className="space-y-5">
      <CaptureActivityPanel activities={captureActivity} />

      <div className="grid min-h-0 grid-cols-[320px_minmax(0,1fr)] gap-5">
        <div className="space-y-2">
          {sessions.length === 0 ? (
            <EmptyCard
              title="No captures yet"
              body="Captures appear here after processing finishes."
            />
          ) : null}
          {sessions.map((session) => (
            <button
              key={session.id}
              type="button"
              onClick={() => onSelect(session.id)}
              className={`w-full rounded-lg border px-3 py-3 text-left ${
                selectedSessionId === session.id
                  ? "border-wiki-accent/70 bg-wiki-accent/10"
                  : "border-wiki-border bg-wiki-bg-2/40 hover:bg-wiki-bg-3"
              }`}
            >
              <div className="font-medium text-wiki-fg-1">{session.title}</div>
              <div className="mt-1 text-xs text-wiki-fg-3">
                {session.device_name ?? "Unknown device"} ·{" "}
                {formatRelativeTime(Number(session.started_at))}
              </div>
              <div className="mt-2 line-clamp-2 text-sm text-wiki-fg-2">
                {session.summary}
              </div>
            </button>
          ))}
        </div>
        <div className="min-w-0">
          {detail ? (
            <SessionDetail api={api} detail={detail} />
          ) : (
            <EmptyCard
              title="Select a capture"
              body="Capture transcript, speaker clusters, and canonical blocks appear here."
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ProcessingWindowsTab({
  windows,
  pendingAudio,
  loading,
  onFinalize,
  onProcessWindow,
}: {
  windows: ProcessingWindowSummary[];
  pendingAudio: PendingAudioChunkGroup[];
  loading: boolean;
  onFinalize: (deviceId: string, processNow: boolean) => Promise<void>;
  onProcessWindow: (windowId: string) => Promise<void>;
}) {
  return (
    <div className="space-y-5">
      <section className="rounded-xl border border-wiki-border bg-wiki-bg-2/30 p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <div className="label-caps">Diagnostics</div>
            <h2 className="text-lg font-semibold text-wiki-fg-1">
              Capture buffers
            </h2>
          </div>
        </div>

        {pendingAudio.length === 0 ? (
          <EmptyCard
            title="No capture buffers"
            body="Unfinalized device audio that has not joined a capture appears here."
          />
        ) : (
          <div className="space-y-2">
            {pendingAudio.map((group) => (
              <div
                key={group.device_id}
                className="rounded-lg border border-wiki-border bg-wiki-bg-3/50 p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-wiki-fg-1">
                      {group.device_name ?? group.device_id}
                    </div>
                    <div className="mt-1 text-xs text-wiki-fg-3">
                      {group.chunk_count} chunks ·{" "}
                      {formatDuration(group.total_duration_ms)} ·{" "}
                      {formatDateTime(group.first_captured_at)} to{" "}
                      {formatDateTime(group.last_captured_at)}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      variant="secondary"
                      disabled={loading}
                      onClick={() => void onFinalize(group.device_id, false)}
                    >
                      Finalize
                    </Button>
                    <Button
                      disabled={loading}
                      onClick={() => void onFinalize(group.device_id, true)}
                    >
                      Finalize + stub
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="rounded-xl border border-wiki-border bg-wiki-bg-2/30 p-4">
        <div className="label-caps mb-3">Capture processing</div>
        {windows.length === 0 ? (
          <EmptyCard
            title="No captures queued"
            body="Closed captures and processing status appear here."
          />
        ) : (
          <div className="space-y-2">
            {windows.map((window) => (
              <div
                key={window.id}
                className="rounded-lg border border-wiki-border bg-wiki-bg-3/40 p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-wiki-fg-1">
                        {window.device_name ?? window.device_id}
                      </span>
                      <span className="rounded-full bg-wiki-bg-hover px-2 py-0.5 text-xs text-wiki-fg-2">
                        {window.status}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-wiki-fg-3">
                      {window.chunk_count} chunks ·{" "}
                      {formatDuration(window.total_duration_ms)} ·{" "}
                      {formatDateTime(window.started_at)}
                    </div>
                    {window.session_title ? (
                      <div className="mt-2 text-sm text-wiki-fg-2">
                        {window.session_title}
                      </div>
                    ) : null}
                    {window.error ? (
                      <div className="mt-2 text-sm text-red-200">
                        {window.error}
                      </div>
                    ) : null}
                  </div>
                  {window.status === "ready" || window.status === "failed" ? (
                    <div className="flex shrink-0 gap-2">
                      <Button
                        disabled={loading}
                        onClick={() => void onProcessWindow(window.id)}
                        title="Diarize + transcribe + match against enrolled voices via the Modal speaker-match pipeline"
                      >
                        {window.status === "failed"
                          ? "Retry processing"
                          : "Run processing"}
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function SessionDetail({
  api,
  detail,
}: {
  api: SpokenMemoryApi;
  detail: SpokenMemorySessionDetail;
}) {
  const [chunks, setChunks] = useState<SessionAudioChunk[]>([]);
  const [chunksError, setChunksError] = useState<string | null>(null);
  const [chunksLoading, setChunksLoading] = useState(false);
  const [currentChunkIdx, setCurrentChunkIdx] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const sessionId = detail.session.id;

  useEffect(() => {
    let cancelled = false;
    setChunks([]);
    setChunksError(null);
    setCurrentChunkIdx(0);
    setIsPlaying(false);
    setChunksLoading(true);
    api
      .listSessionAudioChunks(sessionId)
      .then((result) => {
        if (cancelled) return;
        setChunks(result.chunks);
      })
      .catch((err) => {
        if (cancelled) return;
        setChunksError(String(err));
      })
      .finally(() => {
        if (cancelled) return;
        setChunksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, sessionId]);

  const seekToMs = useCallback(
    (sessionRelativeMs: number) => {
      if (chunks.length === 0) return;
      const chunkIdx = Math.max(
        0,
        chunks.findIndex(
          (chunk, i) =>
            sessionRelativeMs <
              chunk.session_relative_start_ms + chunk.duration_ms ||
            i === chunks.length - 1,
        ),
      );
      setCurrentChunkIdx(chunkIdx);
      setIsPlaying(true);
      const audio = audioRef.current;
      if (!audio) return;
      const chunk = chunks[chunkIdx]!;
      const offsetSec = Math.max(
        0,
        (sessionRelativeMs - chunk.session_relative_start_ms) / 1000,
      );
      if (audio.src !== chunk.presigned_url) {
        audio.src = chunk.presigned_url;
      }
      const seek = () => {
        audio.currentTime = offsetSec;
        audio.play().catch(() => {});
      };
      if (audio.readyState >= 1) {
        seek();
      } else {
        audio.addEventListener("loadedmetadata", seek, { once: true });
      }
    },
    [chunks],
  );

  const playFromStart = useCallback(() => {
    seekToMs(0);
  }, [seekToMs]);

  const stopPlayback = useCallback(() => {
    setIsPlaying(false);
    const audio = audioRef.current;
    if (audio) audio.pause();
  }, []);

  const handleEnded = useCallback(() => {
    setCurrentChunkIdx((prev) => {
      const next = prev + 1;
      if (next >= chunks.length) {
        setIsPlaying(false);
        return prev;
      }
      const audio = audioRef.current;
      if (audio) {
        audio.src = chunks[next]!.presigned_url;
        audio.play().catch(() => {});
      }
      return next;
    });
  }, [chunks]);

  const totalDurationMs = chunks.reduce(
    (sum, chunk) => sum + chunk.duration_ms,
    0,
  );
  const currentChunk = chunks[currentChunkIdx];

  return (
    <div className="space-y-5">
      <section className="rounded-xl border border-wiki-border bg-wiki-bg-2/30 p-4">
        <div className="label-caps">Capture</div>
        <h2 className="text-lg font-semibold text-wiki-fg-1">
          {detail.session.title}
        </h2>
        <p className="mt-2 text-sm text-wiki-fg-2">{detail.session.summary}</p>
      </section>

      <section className="rounded-xl border border-wiki-border bg-wiki-bg-2/30 p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="label-caps">Audio</div>
          <div className="text-xs text-wiki-fg-3">
            {chunks.length > 0
              ? `${chunks.length} chunks · ${formatDuration(totalDurationMs)} of speech`
              : chunksLoading
                ? "Loading…"
                : "No audio available"}
          </div>
        </div>
        {chunksError ? (
          <div className="mb-2 rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
            {chunksError}
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant={isPlaying ? "secondary" : "default"}
            disabled={chunks.length === 0}
            onClick={isPlaying ? stopPlayback : playFromStart}
          >
            {isPlaying ? "Pause" : "▶ Play capture"}
          </Button>
          {currentChunk ? (
            <span className="text-xs text-wiki-fg-3">
              chunk {currentChunkIdx + 1} / {chunks.length} · starts at{" "}
              {formatDuration(currentChunk.session_relative_start_ms)}
            </span>
          ) : null}
        </div>
        <audio
          ref={audioRef}
          onEnded={handleEnded}
          onPause={() => setIsPlaying(false)}
          onPlay={() => setIsPlaying(true)}
          controls
          preload="none"
          className="mt-3 w-full"
        />
        <p className="mt-2 text-xs text-wiki-fg-3">
          Plays the original VAD-trimmed Opus chunks gaplessly. Click any
          transcript line below to jump to that moment. Silence between chunks
          is skipped — the timeline is wall-clock-relative to the capture start.
        </p>
      </section>

      <section className="rounded-xl border border-wiki-border bg-wiki-bg-2/30 p-4">
        <div className="label-caps mb-3">Speaker clusters</div>
        <div className="flex flex-wrap gap-2">
          {detail.speaker_clusters.map((cluster) => (
            <span
              key={cluster.id}
              className="rounded-full bg-wiki-bg-3 px-3 py-1 text-sm text-wiki-fg-2"
            >
              {cluster.matched_member_name ?? cluster.local_label} ·{" "}
              {cluster.match_status}
            </span>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-wiki-border bg-wiki-bg-2/30 p-4">
        <div className="label-caps mb-3">Transcript</div>
        <div className="space-y-3">
          {detail.segments.map((segment) => (
            <button
              key={segment.id}
              type="button"
              onClick={() => seekToMs(segment.start_ms)}
              disabled={chunks.length === 0}
              className="grid w-full grid-cols-[110px_minmax(0,1fr)] gap-3 rounded-md p-2 text-left text-sm transition-colors hover:bg-wiki-bg-3 disabled:cursor-default disabled:hover:bg-transparent"
            >
              <div className="font-medium text-wiki-fg-2">
                {segment.speaker_name ?? segment.local_label ?? "Unknown"}
              </div>
              <div>
                <div className="text-wiki-fg-1">{segment.text}</div>
                <div className="mt-1 text-xs text-wiki-fg-3">
                  {formatDuration(segment.start_ms)}–
                  {formatDuration(segment.end_ms)}
                  {chunks.length > 0 ? " · click to play" : ""}
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-wiki-border bg-wiki-bg-2/30 p-4">
        <div className="label-caps mb-3">Canonical blocks</div>
        <div className="space-y-3">
          {detail.blocks.map((block) => (
            <div key={block.id} className="rounded-lg bg-wiki-bg-3/60 p-3">
              <div className="font-medium text-wiki-fg-1">{block.title}</div>
              <div className="mt-1 text-sm text-wiki-fg-2">{block.summary}</div>
              <pre className="mt-3 rounded bg-black/20 p-3 text-xs whitespace-pre-wrap text-wiki-fg-2">
                {block.text}
              </pre>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function VoiceProfileDiagnosticsPanel({
  api,
  diagnostics,
  onRefresh,
}: {
  api: SpokenMemoryApi;
  diagnostics?: VoiceProfileDiagnostics | null;
  onRefresh: () => Promise<void>;
}) {
  const [refreshing, setRefreshing] = useState(false);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  if (!diagnostics) {
    return (
      <FormCard>
        <FormStackedRow
          label="Match diagnostics"
          hint="Loading profile, worker, and speaker-cluster state."
        >
          <div className="h-16 animate-pulse rounded-md bg-muted/40" />
        </FormStackedRow>
      </FormCard>
    );
  }

  const totals = diagnostics.totals;
  const unresolvedClusters = totals.unmatched_cluster_count;
  const activeProfilesReady =
    totals.active_profile_count > 0 &&
    totals.profiles_with_embeddings === totals.active_profile_count;

  return (
    <FormCard data-testid="voice-profile-diagnostics">
      <FormStackedRow
        label="Match diagnostics"
        hint="Use this to see whether enrolled voices are being used by processed captures."
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            <DiagnosticPill
              ok={activeProfilesReady}
              label={`${totals.active_profile_count} active profile${
                totals.active_profile_count === 1 ? "" : "s"
              }`}
            />
            <DiagnosticPill
              ok={diagnostics.config.modal_configured}
              label="Modal primitives"
            />
            <DiagnosticPill
              ok={unresolvedClusters === 0}
              label={`${unresolvedClusters} needs review`}
            />
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={refreshing}
            onClick={() => void refresh()}
          >
            <RefreshCw
              className={`mr-1.5 h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCell
            label="matched clusters"
            value={totals.matched_cluster_count}
            detail={formatDuration(totals.matched_speech_ms)}
          />
          <MetricCell
            label="unmatched"
            value={totals.unmatched_cluster_count}
            detail={formatDuration(totals.unresolved_speech_ms)}
          />
          <MetricCell
            label="sample audio"
            value={`${totals.profiles_with_sample_audio}/${totals.profile_count}`}
            detail="profiles"
          />
        </div>

        {diagnostics.profiles.length > 0 ? (
          <div className="overflow-hidden rounded-md border border-border bg-background">
            {diagnostics.profiles.map((profile) => (
              <div
                key={profile.id}
                className="grid gap-3 border-b border-border px-3 py-2.5 last:border-b-0 md:grid-cols-[minmax(0,1fr)_160px_160px_220px]"
              >
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-medium text-foreground">
                    {profile.display_name}
                  </div>
                  <div className="mt-0.5 text-[12px] text-muted-foreground">
                    {profile.enrollment_source} · {profile.status} · quality{" "}
                    {profile.quality_score?.toFixed(2) ?? "n/a"}
                  </div>
                </div>
                <div className="text-[12px] text-muted-foreground">
                  <div className="font-medium text-foreground">
                    {profile.matched_cluster_count} matched
                  </div>
                  <div>
                    avg {formatConfidence(profile.average_match_confidence)}
                  </div>
                </div>
                <div className="text-[12px] text-muted-foreground">
                  <div className="font-medium text-foreground">
                    {formatDuration(profile.total_speech_ms)}
                  </div>
                  <div>
                    {profile.last_matched_at
                      ? `last ${formatRelativeTime(profile.last_matched_at)}`
                      : "no matches yet"}
                  </div>
                </div>
                {profile.has_sample_audio ? (
                  <audio
                    controls
                    preload="metadata"
                    crossOrigin="use-credentials"
                    src={api.voiceProfileSampleAudioUrl(profile.id)}
                    className="h-8 w-full min-w-0"
                  />
                ) : (
                  <div className="text-[12px] text-muted-foreground">
                    no sample stored
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-md border border-dashed border-border px-3 py-4 text-[13px] text-muted-foreground">
            No profiles are enrolled in this workspace yet.
          </div>
        )}

        {diagnostics.recent_clusters.length > 0 ? (
          <div className="space-y-2">
            <div className="text-[12px] font-medium text-foreground">
              Recent speaker clusters
            </div>
            <div className="overflow-hidden rounded-md border border-border bg-background">
              {diagnostics.recent_clusters.map((cluster) => (
                <div
                  key={cluster.id}
                  className="grid gap-2 border-b border-border px-3 py-2.5 text-[12px] last:border-b-0 md:grid-cols-[minmax(0,1fr)_120px_110px]"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium text-foreground">
                      {cluster.session_title ?? "Spoken Memory capture"} ·{" "}
                      {cluster.local_label}
                    </div>
                    <div className="mt-0.5 text-muted-foreground">
                      {cluster.matched_member_name ?? "Unassigned"} ·{" "}
                      {cluster.segment_count} segment
                      {cluster.segment_count === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="text-muted-foreground">
                    <span className={clusterStatusClass(cluster.match_status)}>
                      {cluster.match_status.replace("_", " ")}
                    </span>
                    <div className="mt-0.5">
                      {formatConfidence(cluster.match_confidence)}
                    </div>
                  </div>
                  <div className="text-muted-foreground">
                    {formatDuration(cluster.total_speech_ms)}
                    <div className="mt-0.5">
                      {formatRelativeTime(cluster.session_started_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </FormStackedRow>
    </FormCard>
  );
}

function DiagnosticPill({ ok, label }: { ok: boolean; label: string }) {
  const Icon = ok ? CheckCircle2 : CircleAlert;
  return (
    <span
      className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[12px] ${
        ok
          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
          : "border-amber-500/30 bg-amber-500/10 text-amber-300"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </span>
  );
}

function MetricCell({
  label,
  value,
  detail,
}: {
  label: string;
  value: string | number;
  detail: string;
}) {
  return (
    <div className="rounded-md border border-border bg-background px-3 py-2">
      <div className="text-[11px] text-muted-foreground uppercase">{label}</div>
      <div className="mt-1 text-[18px] font-semibold text-foreground">
        {value}
      </div>
      <div className="mt-0.5 text-[12px] text-muted-foreground">{detail}</div>
    </div>
  );
}

function clusterStatusClass(status: string) {
  if (status === "matched") return "text-emerald-300";
  return "text-red-300";
}

function VoiceProfilesTab({
  api = spokenMemoryApi,
  workspaceSlug,
  profiles,
  diagnostics,
  reviewItems,
  showReviewQueue = false,
  claimingClusterId,
  onClaimCluster,
  onRefreshDiagnostics,
  onUploaded,
}: {
  api?: SpokenMemoryApi;
  workspaceSlug?: string | null;
  profiles: VoiceProfile[];
  diagnostics?: VoiceProfileDiagnostics | null;
  reviewItems?: SpeakerClusterReviewItem[];
  showReviewQueue?: boolean;
  claimingClusterId?: string | null;
  onClaimCluster?: (clusterId: string) => Promise<void>;
  onRefreshDiagnostics?: () => Promise<void>;
  onUploaded: () => Promise<void>;
}) {
  const deleteVoiceProfile = useDeleteVoiceProfileMutation(workspaceSlug);
  const [deletingProfileId, setDeletingProfileId] = useState<string | null>(
    null,
  );

  // Group enrollments by the person they belong to (plan 018). Profiles the
  // backfill has not linked yet fall back to the enrolling account's name.
  const profileGroups = useMemo(() => {
    const groups = new Map<
      string,
      { key: string; label: string; profiles: VoiceProfile[] }
    >();
    for (const profile of profiles) {
      const key = profile.person_id ?? `member:${profile.member_name ?? "?"}`;
      const label =
        profile.person_name ?? profile.member_name ?? profile.display_name;
      const group = groups.get(key);
      if (group) group.profiles.push(profile);
      else groups.set(key, { key, label, profiles: [profile] });
    }
    return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [profiles]);

  const deleteProfile = async (profile: VoiceProfile) => {
    const confirmed = window.confirm(
      `Delete the voice profile "${profile.display_name}"?`,
    );
    if (!confirmed) return;
    setDeletingProfileId(profile.id);
    try {
      await deleteVoiceProfile.mutateAsync(profile.id);
      await onUploaded();
    } catch {
      // error is surfaced via the parent refresh failing
    } finally {
      setDeletingProfileId(null);
    }
  };

  return (
    <>
      <VoiceProfileDiagnosticsPanel
        api={api}
        diagnostics={diagnostics}
        onRefresh={onRefreshDiagnostics ?? onUploaded}
      />

      {showReviewQueue ? (
        <FormCard>
          <FormStackedRow
            label="Needs review"
            hint="Low-confidence and unmatched speaker clusters."
          >
            {reviewItems && reviewItems.length > 0 ? (
              <div className="space-y-2">
                {reviewItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-background/60 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-foreground">
                        {item.session_title ?? "Spoken Memory capture"} ·{" "}
                        {item.local_label}
                      </div>
                      <div className="mt-1 text-[12px] text-muted-foreground">
                        {formatDateTime(item.session_started_at)} ·{" "}
                        {item.segment_count} segments ·{" "}
                        {formatDuration(item.total_speech_ms)} speech · no
                        candidate above the match threshold
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={
                        !onClaimCluster || claimingClusterId === item.id
                      }
                      onClick={() => void onClaimCluster?.(item.id)}
                    >
                      {claimingClusterId === item.id
                        ? "Claiming..."
                        : "This is me"}
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-[13px] text-muted-foreground">
                No speaker matches need review.
              </div>
            )}
          </FormStackedRow>
        </FormCard>
      ) : null}

      <FormCard>
        <FormStackedRow
          label="Profiles"
          hint={
            profiles.length === 0
              ? "No voice profiles yet. Record a voice test in Settings to add your first."
              : `${profiles.length} profile${profiles.length === 1 ? "" : "s"} enrolled across ${profileGroups.length} ${profileGroups.length === 1 ? "person" : "people"}.`
          }
        >
          {profileGroups.length > 0 ? (
            <div className="space-y-3">
              {profileGroups.map((group) => (
                <div key={group.key}>
                  <div className="mb-1 text-[12px] font-medium text-muted-foreground">
                    {group.label}
                  </div>
                  <ul className="divide-y divide-border overflow-hidden rounded-md border border-border bg-background">
                    {group.profiles.map((profile) => (
                      <VoiceProfileListItem
                        key={profile.id}
                        api={api}
                        profile={profile}
                        deleting={deletingProfileId === profile.id}
                        onDelete={() => void deleteProfile(profile)}
                      />
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : null}
        </FormStackedRow>
      </FormCard>
    </>
  );
}

function VoiceProfileListItem({
  api,
  profile,
  deleting,
  onDelete,
}: {
  api: SpokenMemoryApi;
  profile: VoiceProfile;
  deleting: boolean;
  onDelete: () => void;
}) {
  const readiness = voiceProfileReadiness(profile);
  return (
    <li className="flex items-center gap-3 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <div className="truncate text-[13px] font-medium text-foreground">
            {profile.display_name}
          </div>
          <VoiceProfileReadinessBadge readiness={readiness} />
        </div>
        <div className="mt-0.5 text-[12px] text-muted-foreground">
          {profile.enrollment_source} · {profile.status} ·{" "}
          {profile.representation ?? profile.embedding_model}
        </div>
        <div className="mt-0.5 text-[12px] text-muted-foreground">
          quality {profile.quality_score?.toFixed(2) ?? "n/a"} ·{" "}
          {formatDuration(profile.total_speech_ms)}
        </div>
        {readiness.tone !== "ready" ? (
          <div className="mt-1 text-[12px] text-amber-200">
            {readiness.detail}
          </div>
        ) : null}
      </div>
      {profile.has_sample_audio ? (
        <audio
          controls
          preload="metadata"
          crossOrigin="use-credentials"
          src={api.voiceProfileSampleAudioUrl(profile.id)}
          className="h-8 w-[220px] max-w-full"
        />
      ) : null}
      <Button variant="ghost" size="sm" disabled={deleting} onClick={onDelete}>
        {deleting ? "Deleting..." : "Delete"}
      </Button>
    </li>
  );
}

type VoiceProfileReadiness = {
  label: string;
  detail: string;
  tone: "ready" | "warning";
};

function voiceProfileReadiness(profile: VoiceProfile): VoiceProfileReadiness {
  if (
    profile.attribution_ready === true ||
    profile.attribution_status === "ready"
  ) {
    return {
      label: "Ready",
      detail: "Ready for speaker attribution.",
      tone: "ready",
    };
  }
  if (profile.attribution_status === "needs_redo") {
    return {
      label: "Needs review",
      detail:
        profile.attribution_status_reason ??
        profile.sample_status_reason ??
        "Voice sample needs review.",
      tone: "warning",
    };
  }
  if (profile.attribution_status === "missing_hosted_voiceprint") {
    return {
      label: "Not ready",
      detail:
        profile.attribution_status_reason ??
        "Missing hosted voiceprint for speaker attribution.",
      tone: "warning",
    };
  }
  if (profile.sample_status === "needs_redo") {
    return {
      label: "Needs review",
      detail: profile.sample_status_reason ?? "Voice sample needs review.",
      tone: "warning",
    };
  }
  return {
    label: "Not ready",
    detail: "Speaker attribution readiness is unknown.",
    tone: "warning",
  };
}

function VoiceProfileReadinessBadge({
  readiness,
}: {
  readiness: VoiceProfileReadiness;
}) {
  const Icon = readiness.tone === "ready" ? CheckCircle2 : CircleAlert;
  const className =
    readiness.tone === "ready"
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
      : "border-amber-500/30 bg-amber-500/10 text-amber-200";
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${className}`}
    >
      <Icon className="h-3 w-3" />
      {readiness.label}
    </span>
  );
}

function EmptyCard({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-dashed border-wiki-border bg-wiki-bg-2/20 p-5">
      <div className="font-medium text-wiki-fg-1">{title}</div>
      <div className="mt-1 text-sm text-wiki-fg-3">{body}</div>
    </div>
  );
}
